import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Container, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import PublicSpaceProfileHeader from '../../components/PublicSpaceProfileHeader';
import PublicSpaceRoomList from '../../components/PublicSpaceRoomList';
import PublicTutorsRoomPanel from '../../components/PublicTutorsRoomPanel';
import SEO from '../../components/SEO';
import TutorBookingStepperDialog from '../../components/TutorBookingStepperDialog';
import type { TutorSearchResult } from '../../models/Tutor';
import {
  type SpaceTutorsPageResolution,
  useSpaceTutorsPageData,
} from '../../hooks/useSpaceTutorsPageData';
import { tutorListingPath } from '../../utils/searchPaths';
import { trackStartBooking, trackViewProvider } from '../../utils/analytics';

export interface SpaceTutorsPageShellProps {
  resolution: SpaceTutorsPageResolution | null;
}

const SpaceTutorsPageShell: React.FC<SpaceTutorsPageShellProps> = ({ resolution }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    space,
    reviewStats,
    spaceReviews,
    spaceReviewsLoading,
    tutors,
    loading,
    spaceError,
    tutorError,
    tutorReviewsById,
    tutorReviewsLoadingById,
    loadTutorReviews,
  } = useSpaceTutorsPageData(resolution);
  const [selectedTutor, setSelectedTutor] = useState<TutorSearchResult | null>(null);
  const [selectedLessonOptionId, setSelectedLessonOptionId] = useState<string | null>(null);

  const currentPath = `${location.pathname}${location.search}`;

  const openTutorProfile = (tutor: TutorSearchResult) => {
    navigate(tutorListingPath(tutor.tutorId), {
      state: {
        tutor,
        searchBackPath: currentPath,
      },
    });
  };

  const openBooking = (tutor: TutorSearchResult, lessonOptionId?: string | null) => {
    trackStartBooking({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
      city: space?.city,
      booking_value: tutor.lessonOptions?.[0]?.price,
      currency: tutor.lessonOptions?.[0]?.lessonPriceCurrency ?? 'PLN',
    });
    setSelectedTutor(tutor);
    setSelectedLessonOptionId(lessonOptionId ?? null);
  };

  useEffect(() => {
    if (!space) return;
    trackViewProvider({
      provider_type: 'space',
      provider_id: space.id,
      city: space.city,
      provider_name: space.name,
    });
  }, [space?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (spaceError || !space) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{spaceError ?? t('schoolPage.loadError', "Couldn't load school details.")}</Alert>
      </Container>
    );
  }

  const roomCount = space.rooms?.length ?? 0;
  const spaceImages = space.imageUrls ?? [];

  return (
    <>
      <SEO
        title={t('schoolPage.seoTitle', '{{name}} tutors', { name: space.name })}
        description={
          space.description?.trim() ||
          t('schoolPage.seoDescription', 'Book lessons with tutors at {{name}}.', { name: space.name })
        }
        image={spaceImages[0]}
        noindex={true}
      />
      <Container maxWidth={false} sx={{ maxWidth: 1120, py: { xs: 1.5, md: 2 }, pb: { xs: 6, md: 8 } }}>
        <Stack spacing={1.5}>
          <PublicSpaceProfileHeader
            space={space}
            reviewStats={reviewStats}
            spaceReviews={spaceReviews}
            spaceReviewsLoading={spaceReviewsLoading}
          />

          <PublicTutorsRoomPanel
            labelsPrefix="schoolPage"
            tutors={tutors}
            tutorError={tutorError}
            roomCount={roomCount}
            renderRooms={(viewMode) => <PublicSpaceRoomList space={space} viewMode={viewMode} />}
            tutorReviewsById={tutorReviewsById}
            tutorReviewsLoadingById={tutorReviewsLoadingById}
            loadTutorReviews={loadTutorReviews}
            onOpenTutorProfile={openTutorProfile}
            onOpenBooking={openBooking}
          />
        </Stack>
      </Container>

      <TutorBookingStepperDialog
        open={Boolean(selectedTutor)}
        tutor={selectedTutor}
        initialLessonOptionId={selectedLessonOptionId}
        onClose={() => {
          setSelectedTutor(null);
          setSelectedLessonOptionId(null);
        }}
      />
    </>
  );
};

export default SpaceTutorsPageShell;
